import React, { useState } from "react";
import Button from "./Button";
import { ROLE_OWNER, VIEW_SCOPE_WORKSPACE, VIEW_SCOPE_WORKSTREAM } from "../domainCatalog";

const AppLayout = ({
  children,
  subscription,
  onOpenChat,
  onOpenMeeting,
  onOpenTasks,
  onOpenBoard,
  onOpenContext,
  onOpenWbs,
  onOpenTeam,
  onOpenGantt,
  onOpenPresentation,
  onOpenAdmin,
  isAdmin,
  onOpenSettings,
  availableModels = [],
  selectedModel,
  onModelChange = () => {},
  projects = [],
  activeProjectId,
  onProjectChange = () => {},
  onCreateProject = () => {},
  viewScope = VIEW_SCOPE_WORKSTREAM,
  onViewScopeChange = () => {},
  onLogout,
  chatUnreadCount = 0,
  chatToast,
  onOpenChatToast = () => {},
  activeMeeting,
  meetingToast,
  onOpenMeetingToast = () => {}
}) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem("taskit.mainSidebarCollapsed") === "true");
  const subscriptionLabel = subscription?.plan
    ? `${subscription.plan.name} · ${subscription.project_count}/${subscription.plan.project_limit} workstreams · ${subscription.member_count}/${subscription.plan.member_limit} invites`
    : "Loading plan...";
  const isOwner = subscription?.current_role === ROLE_OWNER;
  const navItems = [
    { label: "Chat", icon: "chat", onClick: onOpenChat, badge: chatUnreadCount },
    { label: "Meetings", icon: "meeting", onClick: onOpenMeeting, live: Boolean(activeMeeting) },
    { label: "Project Context", icon: "context", onClick: onOpenContext },
    { label: "WBS Studio", icon: "wbs", onClick: onOpenWbs },
    { label: "Team Studio", icon: "team", onClick: onOpenTeam },
    { label: "Task Studio", icon: "task", onClick: onOpenTasks },
    { label: "Board", icon: "board", onClick: onOpenBoard },
    { label: "Gantt Studio", icon: "gantt", onClick: onOpenGantt },
    { label: "Presentation Studio", icon: "presentation", onClick: onOpenPresentation },
    ...(isOwner ? [{ label: "Settings", icon: "settings", onClick: onOpenSettings }] : []),
    ...(isAdmin ? [{ label: "Admin", icon: "admin", onClick: onOpenAdmin }] : [])
  ];

  return (
    <div style={{ height: "100vh", display: "flex", background: "#030712", color: "white", fontFamily: "Arial" }}>
      <aside
        style={{
          width: sidebarCollapsed ? "78px" : "260px",
          padding: sidebarCollapsed ? "22px 14px" : "24px",
          boxSizing: "border-box",
          transition: "width 0.18s ease, padding 0.18s ease"
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: sidebarCollapsed ? "center" : "space-between", gap: "10px", marginBottom: "24px" }}>
          {!sidebarCollapsed && <h2 style={{ margin: 0 }}>Taskit</h2>}
          <button
            aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            onClick={() => setSidebarCollapsed((collapsed) => {
              localStorage.setItem("taskit.mainSidebarCollapsed", String(!collapsed));
              return !collapsed;
            })}
            style={{
              width: "30px",
              height: "30px",
              display: "grid",
              placeItems: "center",
              border: 0,
              background: "transparent",
              color: "#cbd5e1",
              cursor: "pointer",
              padding: 0
            }}
          >
            <LayoutIcon name={sidebarCollapsed ? "chevronRight" : "chevronLeft"} />
          </button>
        </div>

        <nav aria-label="Main navigation" style={{ display: "grid", gap: "6px" }}>
          {navItems.map((item) => (
            <SidebarItem key={item.label} {...item} collapsed={sidebarCollapsed} />
          ))}
        </nav>
      </aside>

      <main style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <div
          style={{
            height: "72px",
            padding: "0 32px",
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: "12px",
            borderBottom: "1px solid rgba(255,255,255,0.08)"
          }}
        >
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              color: "#cbd5e1",
              fontSize: "13px",
              fontWeight: 700
            }}
          >
            Workstream
            <select
              aria-label="Active workstream"
              value={activeProjectId || ""}
              onChange={(event) => onProjectChange(Number(event.target.value))}
              style={{
                minWidth: "190px",
                padding: "9px 10px",
                border: "1px solid rgba(255,255,255,0.16)",
                borderRadius: "9px",
                background: "#111827",
                color: "white"
              }}
            >
              {projects.map((project) => (
                <option key={project.id} value={project.id}>{project.name}</option>
              ))}
            </select>
          </label>
          <button
            aria-label="Create workstream"
            onClick={onCreateProject}
            style={{
              padding: "9px 12px",
              border: "1px solid rgba(255,255,255,0.16)",
              borderRadius: "9px",
              background: "#1d4ed8",
              color: "white",
              cursor: "pointer",
              fontWeight: 700
            }}
          >
            + Workstream
          </button>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              color: "#cbd5e1",
              fontSize: "13px",
              fontWeight: 700
            }}
          >
            View
            <select
              aria-label="Studio view scope"
              value={viewScope}
              onChange={(event) => onViewScopeChange(event.target.value)}
              style={{
                minWidth: "150px",
                padding: "9px 10px",
                border: "1px solid rgba(255,255,255,0.16)",
                borderRadius: "9px",
                background: "#111827",
                color: "white"
              }}
            >
              <option value={VIEW_SCOPE_WORKSTREAM}>Workstream</option>
              <option value={VIEW_SCOPE_WORKSPACE}>Workspace</option>
            </select>
          </label>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              color: "#cbd5e1",
              fontSize: "13px",
              fontWeight: 700
            }}
          >
            Model
            <select
              aria-label="AI model"
              value={selectedModel}
              onChange={(event) => onModelChange(event.target.value)}
              style={{
                minWidth: "170px",
                padding: "9px 10px",
                border: "1px solid rgba(255,255,255,0.16)",
                borderRadius: "9px",
                background: "#111827",
                color: "white"
              }}
            >
              {availableModels.map((model) => (
                <option key={model.id} value={model.id}>{model.label}</option>
              ))}
            </select>
          </label>
          <Button label={subscriptionLabel} />
          <button
            aria-label="Logout"
            title="Logout"
            onClick={onLogout}
            style={{
              height: "40px",
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              padding: "0 14px",
              border: "1px solid rgba(248,113,113,0.35)",
              borderRadius: "999px",
              background: "linear-gradient(135deg, rgba(127,29,29,0.92), rgba(30,41,59,0.96))",
              color: "#fee2e2",
              cursor: "pointer",
              fontWeight: 800,
              letterSpacing: "0.01em",
              boxShadow: "0 12px 30px rgba(127,29,29,0.22), inset 0 1px 0 rgba(255,255,255,0.1)"
            }}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={{ width: "18px", height: "18px", fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" }}>
              <path d="M10 17l5-5-5-5" />
              <path d="M15 12H3" />
              <path d="M14 4h4a3 3 0 013 3v10a3 3 0 01-3 3h-4" />
            </svg>
            Logout
          </button>
        </div>

        <div style={{ flex: 1, overflow: "auto" }}>
          {children}
        </div>
        {chatToast && <button onClick={onOpenChatToast} style={{ position: "fixed", right: "22px", bottom: "22px", zIndex: 1000, width: "320px", padding: "14px 16px", border: "1px solid rgba(129,140,248,.55)", borderRadius: "12px", background: "#1e293b", color: "white", boxShadow: "0 18px 45px rgba(0,0,0,.4)", textAlign: "left", cursor: "pointer" }}><strong style={{ display: "block", marginBottom: "5px" }}>{chatToast.sender}</strong><span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "#cbd5e1" }}>{chatToast.body}</span></button>}
        {meetingToast && <button onClick={onOpenMeetingToast} style={{ position: "fixed", right: "22px", bottom: "22px", zIndex: 1001, width: "320px", padding: "15px 16px", border: "1px solid rgba(74,222,128,.5)", borderRadius: "12px", background: "#14532d", color: "white", boxShadow: "0 18px 45px rgba(0,0,0,.4)", textAlign: "left", cursor: "pointer" }}><strong style={{ display: "block", marginBottom: "5px" }}>Meeting started</strong><span>{meetingToast.title} · Join now</span></button>}
      </main>
    </div>
  );
};

const SidebarItem = ({ label, icon, onClick, collapsed, badge = 0, live = false }) => (
  <button
    onClick={onClick}
    aria-label={badge ? `${label}, ${badge} unread message${badge === 1 ? "" : "s"}` : label}
    title={collapsed ? label : undefined}
    style={{
      width: "100%",
      position: "relative",
      minHeight: "42px",
      display: "flex",
      alignItems: "center",
      justifyContent: collapsed ? "center" : "flex-start",
      gap: "10px",
      padding: collapsed ? "10px" : "10px 12px",
      border: 0,
      borderRadius: "12px",
      background: "transparent",
      color: "#e5e7eb",
      cursor: "pointer",
      fontWeight: 750,
      textAlign: "left"
    }}
  >
    <span style={{ width: "20px", height: "20px", display: "grid", placeItems: "center", color: "#ffffff", flex: "0 0 auto" }}><LayoutIcon name={icon} /></span>
    <span style={collapsed ? visuallyHidden : { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{label}</span>
    {!!badge && <span style={{ marginLeft: collapsed ? 0 : "auto", position: collapsed ? "absolute" : "static", right: collapsed ? "7px" : undefined, top: collapsed ? "4px" : undefined, minWidth: "17px", height: "17px", padding: "0 4px", boxSizing: "border-box", display: "grid", placeItems: "center", borderRadius: "20px", background: "#ef4444", color: "white", fontSize: "10px", fontWeight: 900 }}>{badge > 99 ? "99+" : badge}</span>}
    {live && <span title="Meeting live" style={{ marginLeft: collapsed ? 0 : "auto", position: collapsed ? "absolute" : "static", right: collapsed ? "7px" : undefined, top: collapsed ? "5px" : undefined, width: "9px", height: "9px", borderRadius: "50%", background: "#ef4444", boxShadow: "0 0 0 4px rgba(239,68,68,.18)" }} />}
  </button>
);

const visuallyHidden = {
  position: "absolute",
  width: "1px",
  height: "1px",
  padding: 0,
  margin: "-1px",
  overflow: "hidden",
  clip: "rect(0,0,0,0)",
  whiteSpace: "nowrap",
  border: 0
};

const LayoutIcon = ({ name }) => {
  const icons = {
    chat: <path d="M4 5h16v11H8l-4 4V5zm4 4h8M8 12h5" />,
    meeting: <path d="M4 7h11v10H4V7zm11 3l5-3v10l-5-3v-4z" />,
    context: <path d="M6 3h9l3 3v15H6V3zm8 0v4h4M9 11h6M9 15h6M9 19h4" />,
    wbs: <path d="M12 4v4m0 0H6v4m6-4h6v4M4 12h4v4H4v-4zm8 0h4v4h-4v-4zm8 0h-4v4h4v-4z" />,
    team: <path d="M8 11a3 3 0 100-6 3 3 0 000 6zm8 0a3 3 0 100-6 3 3 0 000 6zM3 20a5 5 0 0110 0m-2 0a5 5 0 0110 0" />,
    task: <path d="M5 5h14v14H5V5zm3 4h8M8 13h5M8 17h7" />,
    board: <path d="M4 5h4v14H4V5zm6 0h4v9h-4V5zm6 0h4v12h-4V5z" />,
    gantt: <path d="M4 6h16M4 12h16M4 18h16M7 4v4m5 2v4m5 2v4" />,
    presentation: <path d="M4 5h16v11H4V5zm8 11v4m-4 0h8M8 9h8M8 12h5" />,
    settings: <path d="M12 8a4 4 0 100 8 4 4 0 000-8zm0-5v3m0 12v3M4.2 4.2l2.1 2.1m11.4 11.4l2.1 2.1M3 12h3m12 0h3M4.2 19.8l2.1-2.1M17.7 6.3l2.1-2.1" />,
    admin: <path d="M12 3l8 4v5c0 5-3.4 8-8 9-4.6-1-8-4-8-9V7l8-4zm-3 9l2 2 4-5" />,
    chevronLeft: <path d="M15 6l-6 6 6 6" />,
    chevronRight: <path d="M9 6l6 6-6 6" />
  };
  return <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={{ width: "20px", height: "20px", fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" }}>{icons[name]}</svg>;
};

export default AppLayout;
